import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SHEET_CLOSE_ARIA_LABEL } from '../../constants/sheetCopy';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { FILLS, FONTS, RADII, SEARCH_LAYER } from '../../styles/tokens';
import { SearchTab, WHERE_FIELD_COPY, summariseTripSearch } from '../../utils/tripSearchResults';
import { Sheet, SheetTitle } from '../common/Sheet';
import { WhenPanel, WherePanel, WhoPanel } from './MobileTripSearchPanels';
import { TripSearchOpenHandle } from './tripSearchOpenHandle';
import { useTripSearchEditor } from './useTripSearchEditor';

interface MobileTripSearchProps {
  tab: SearchTab;
  initialText?: string;
  openRef?: React.Ref<TripSearchOpenHandle>;
}

type SearchField = 'where' | 'when' | 'who';

const BAR_PROMPT = 'Start your search';
/** The bar's label on screens too narrow for the full prompt. */
const NARROW_BAR_PROMPT = 'Search';
/** Widest screen on which the app-bar pill is too narrow for the full prompt. */
const SHORT_PROMPT_MAX_WIDTH_PX = 427;
const NARROW_SCREEN_QUERY = `(max-width: ${SHORT_PROMPT_MAX_WIDTH_PX}px)`;
const COLLAPSED_ROW_HEIGHT = 56;
const TARGET_SIZE = 44;
/** The app bar's pill: as tall as a tap target and inside the 56px bar. */
const BAR_HEIGHT = TARGET_SIZE;
const SEARCH_BUTTON_HEIGHT = 48;
const BODY_INSET = 16;
const CARD_GAP = 12;

const SHEET_TITLES: Record<SearchTab, string> = {
  discover: 'Plan your trip',
  stays: 'Search stays',
  services: 'Search services',
};

/** When and Who ask the same on every tab; the first field's question comes from `WHERE_FIELD_COPY`. */
const FIELD_QUESTIONS: Record<Exclude<SearchField, 'where'>, string> = {
  when: "When's your trip?",
  who: "Who's coming?",
};

/** When and Who read the same on every tab; the first field's name comes from `WHERE_FIELD_COPY`. */
const FIELD_LABELS: Record<Exclude<SearchField, 'where'>, string> = {
  when: 'When',
  who: 'Who',
};

const SearchGlyph: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

const CloseGlyph: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

interface SearchCardProps {
  field: SearchField;
  label: string;
  question: string;
  value: string;
  isExpanded: boolean;
  onExpand: (field: SearchField) => void;
  children: React.ReactNode;
}

const cardStyle: React.CSSProperties = {
  background: SEARCH_LAYER.card,
  borderRadius: RADII.card,
  flexShrink: 0,
};

const SearchCard: React.FC<SearchCardProps> = ({
  field,
  label,
  question,
  value,
  isExpanded,
  onExpand,
  children,
}) => {
  if (!isExpanded) {
    return (
      <button
        type="button"
        className="focus-ring"
        onClick={() => onExpand(field)}
        style={{
          ...cardStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          width: '100%',
          minHeight: COLLAPSED_ROW_HEIGHT,
          padding: '0 20px',
          border: 'none',
          fontFamily: FONTS.body,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        <span style={{ color: SEARCH_LAYER.inkMuted }}>{label}</span>
        <span
          style={{
            color: SEARCH_LAYER.ink,
            fontWeight: 500,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
      </button>
    );
  }
  return (
    <section style={{ ...cardStyle, padding: 20 }}>
      <h3
        tabIndex={-1}
        className="trip-search-card-heading"
        style={{
          margin: '0 0 16px',
          outline: 'none',
          fontFamily: FONTS.display,
          fontSize: 24,
          fontWeight: 500,
          color: SEARCH_LAYER.ink,
        }}
      >
        {question}
      </h3>
      {children}
    </section>
  );
};

interface TripSearchSheetHeaderProps {
  title: string;
  onClose: () => void;
}

const TripSearchSheetHeader: React.FC<TripSearchSheetHeaderProps> = ({ title, onClose }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: `8px ${BODY_INSET}px`,
      flexShrink: 0,
    }}
  >
    <button
      type="button"
      className="focus-ring"
      aria-label={SHEET_CLOSE_ARIA_LABEL}
      onClick={onClose}
      style={{
        width: TARGET_SIZE,
        height: TARGET_SIZE,
        display: 'grid',
        placeItems: 'center',
        background: 'none',
        border: 'none',
        borderRadius: RADII.control,
        color: SEARCH_LAYER.ink,
        cursor: 'pointer',
      }}
    >
      <CloseGlyph />
    </button>
    <SheetTitle
      style={{
        margin: 0,
        fontFamily: FONTS.display,
        fontSize: 20,
        fontWeight: 500,
        color: SEARCH_LAYER.ink,
      }}
    >
      {title}
    </SheetTitle>
  </div>
);

interface TripSearchSheetFooterProps {
  onClearAll: () => void;
  onSearch: () => void;
  // Set while When is open: the calendar fills the screen and pushes Who below
  // it, so the primary button steps on to Who instead of searching.
  onNext?: () => void;
}

const TripSearchSheetFooter: React.FC<TripSearchSheetFooterProps> = ({
  onClearAll,
  onSearch,
  onNext,
}) => (
  <div
    style={{
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `12px ${BODY_INSET}px`,
      borderTop: `1px solid ${SEARCH_LAYER.stroke}`,
    }}
  >
    <button
      type="button"
      className="focus-ring"
      onClick={onClearAll}
      style={{
        minHeight: TARGET_SIZE,
        padding: '0 8px',
        background: 'none',
        border: 'none',
        borderRadius: RADII.control,
        color: SEARCH_LAYER.ink,
        fontFamily: FONTS.body,
        fontSize: 14,
        fontWeight: 600,
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      Clear all
    </button>
    <button
      type="button"
      className="focus-ring"
      onClick={onNext ?? onSearch}
      // The month calendar above has its own "Next" button.
      aria-label={onNext ? 'Next: Who' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: SEARCH_BUTTON_HEIGHT,
        padding: '0 24px',
        border: 'none',
        borderRadius: RADII.control,
        background: FILLS.gold,
        color: SEARCH_LAYER.ink,
        fontFamily: FONTS.body,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {onNext ? (
        'Next'
      ) : (
        <>
          <SearchGlyph color={SEARCH_LAYER.ink} />
          Search
        </>
      )}
    </button>
  </div>
);

/**
 * The phone trip search: a cream bar that opens a full-screen Where / When /
 * Who sheet. Every pick is saved live, so closing keeps the edits; Search
 * lands on the tab's results.
 */
const MobileTripSearch: React.FC<MobileTripSearchProps> = ({ tab, initialText = '', openRef }) => {
  const editor = useTripSearchEditor(tab);
  const navigate = useNavigate();
  const isNarrowScreen = useMediaQuery(NARROW_SCREEN_QUERY);
  const { tripSearch } = editor;
  const [isOpen, setIsOpen] = useState(false);
  const [expandedField, setExpandedField] = useState<SearchField>('where');
  const [typedText, setTypedText] = useState(initialText);
  // The page's search text lives in the URL, so a cleared or changed `search=`
  // (Clear Search, Back) must reach the bar and the next Search. An effect
  // reseeds only the typed text; remounting on a key would also drop whether
  // the sheet is open and which card is expanded.
  useEffect(() => {
    setTypedText(initialText);
  }, [initialText]);
  const cardStackRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusQuestionRef = useRef(false);

  // Expanding a card unmounts the row that was tapped, so focus is handed to
  // the new card's question instead of falling to the document body.
  useEffect(() => {
    if (!shouldFocusQuestionRef.current) return;
    shouldFocusQuestionRef.current = false;
    cardStackRef.current?.querySelector<HTMLElement>('.trip-search-card-heading')?.focus();
  }, [expandedField]);

  const expandField = (field: SearchField) => {
    shouldFocusQuestionRef.current = true;
    setExpandedField(field);
  };

  const summary = summariseTripSearch(tripSearch, typedText, tab);
  const showsShortPrompt = isNarrowScreen && !summary.barLine;
  const closeSheet = () => setIsOpen(false);
  const openSheet = () => {
    setExpandedField('where');
    setIsOpen(true);
  };
  useImperativeHandle(openRef, () => ({ open: openSheet }), []);
  const browseServices = () => {
    closeSheet();
    navigate(
      `/services?${new URLSearchParams({ destination: tripSearch.destination }).toString()}`,
      {
        state: { landOnResults: true },
      },
    );
  };
  const submit = () => {
    closeSheet();
    editor.submit(typedText);
  };

  return (
    <>
      <button
        type="button"
        className="focus-ring"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        // The short visible label still carries the full name.
        aria-label={showsShortPrompt ? BAR_PROMPT : undefined}
        onClick={openSheet}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: '100%',
          height: BAR_HEIGHT,
          boxSizing: 'border-box',
          padding: '0 14px',
          border: 'none',
          borderRadius: RADII.pill,
          background: FILLS.textPrimary,
          color: FILLS.page,
          fontFamily: FONTS.body,
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <SearchGlyph color={FILLS.page} />
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary.barLine || (showsShortPrompt ? NARROW_BAR_PROMPT : BAR_PROMPT)}
        </span>
      </button>
      {isOpen && (
        <Sheet layout="fullScreen" onClose={closeSheet} surface="cream">
          {/* The cards take the card fill so they read as cards on the cream sheet. */}
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              background: SEARCH_LAYER.surface,
            }}
          >
            <TripSearchSheetHeader title={SHEET_TITLES[tab]} onClose={closeSheet} />
            <div
              ref={cardStackRef}
              style={{
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                padding: `8px ${BODY_INSET}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: CARD_GAP,
              }}
            >
              <SearchCard
                field="where"
                label={WHERE_FIELD_COPY[tab].label}
                question={WHERE_FIELD_COPY[tab].question}
                value={summary.whereValue}
                isExpanded={expandedField === 'where'}
                onExpand={expandField}
              >
                <WherePanel
                  query={tripSearch.destination || typedText}
                  onQueryChange={(query) => {
                    setTypedText(query);
                    if (tripSearch.destination) editor.setDestination('');
                  }}
                  onPickDestination={(destination) => {
                    editor.setDestination(destination);
                    setTypedText('');
                    expandField('when');
                  }}
                  onNavigateListing={closeSheet}
                  onSubmit={submit}
                  destination={tripSearch.destination}
                  onBrowseServices={browseServices}
                  tab={tab}
                />
              </SearchCard>
              <SearchCard
                field="when"
                label={FIELD_LABELS.when}
                question={FIELD_QUESTIONS.when}
                value={summary.whenValue}
                isExpanded={expandedField === 'when'}
                onExpand={expandField}
              >
                <WhenPanel
                  checkin={tripSearch.checkin}
                  checkout={tripSearch.checkout}
                  onDatesChange={editor.setDates}
                />
              </SearchCard>
              <SearchCard
                field="who"
                label={FIELD_LABELS.who}
                question={FIELD_QUESTIONS.who}
                value={summary.whoValue}
                isExpanded={expandedField === 'who'}
                onExpand={expandField}
              >
                <WhoPanel
                  adults={tripSearch.adults}
                  childCount={tripSearch.children}
                  onAdultsChange={editor.setAdults}
                  onChildrenChange={editor.setChildren}
                />
              </SearchCard>
            </div>
            <TripSearchSheetFooter
              onClearAll={() => {
                setTypedText('');
                editor.clearAll();
              }}
              onSearch={submit}
              onNext={expandedField === 'when' ? () => expandField('who') : undefined}
            />
          </div>
        </Sheet>
      )}
    </>
  );
};

export default MobileTripSearch;
